/**
 * Helpers purs pour la vue agenda mécano (formulaire RDV).
 *
 * Convention projet « heure-mur = UTC » : tous les instants planifiés
 * (creneauDebut/Fin, bornes de journée) sont manipulés via leurs composants UTC.
 * Ces helpers ne raisonnent qu'en epoch (getTime), donc indépendants du fuseau hôte.
 *
 * Cf. spec docs/superpowers/specs/2026-06-02-agenda-mecano-rdv-design.md
 * Aligné sur backend/src/services/scheduling.ts (granularité 15 min, plage continue).
 */

export const GRANULARITY_MIN = 15;

/** Créneau occupé (un RDV du mécano) ramené à ses bornes temporelles. */
export type Busy = { debut: Date; fin: Date };

/** Trou libre dans la journée du mécano. */
export type FreeZone = { debut: Date; fin: Date };

/**
 * Calcule les zones libres entre `dayStart` et `dayEnd` en retirant les créneaux
 * occupés. Les blocs occupés sont d'abord rognés à la plage du jour, triés, puis
 * fusionnés (chevauchements ET adjacences), et on renvoie les intervalles restants.
 *
 * @param busy     créneaux occupés (ordre quelconque, peuvent déborder de la plage)
 * @param dayStart borne basse UTC (ex. 09:00 du jour)
 * @param dayEnd   borne haute UTC (ex. 18:00 du jour)
 */
export function computeFreeZones(busy: Busy[], dayStart: Date, dayEnd: Date): FreeZone[] {
  const startMs = dayStart.getTime();
  const endMs = dayEnd.getTime();

  const clipped = busy
    .map((b) => ({
      debut: Math.max(b.debut.getTime(), startMs),
      fin: Math.min(b.fin.getTime(), endMs),
    }))
    .filter((b) => b.fin > b.debut)
    .sort((a, b) => a.debut - b.debut);

  // Fusion des occupés (chevauchement ou adjacence : debut <= fin courant)
  const merged: Array<{ debut: number; fin: number }> = [];
  for (const b of clipped) {
    const last = merged[merged.length - 1];
    if (last && b.debut <= last.fin) {
      if (b.fin > last.fin) last.fin = b.fin;
    } else {
      merged.push({ debut: b.debut, fin: b.fin });
    }
  }

  // Trous entre les blocs fusionnés
  const zones: FreeZone[] = [];
  let cursor = startMs;
  for (const b of merged) {
    if (b.debut > cursor) {
      zones.push({ debut: new Date(cursor), fin: new Date(b.debut) });
    }
    if (b.fin > cursor) cursor = b.fin;
  }
  if (cursor < endMs) {
    zones.push({ debut: new Date(cursor), fin: new Date(endMs) });
  }
  return zones;
}

/**
 * À partir d'un instant cliqué dans une zone libre, cale le début sur le quart
 * d'heure (arrondi inférieur sur la grille :00/:15/:30/:45), puis clampe pour que
 * `[début, début+durée]` rentre entièrement dans la zone.
 *
 * @returns le Date de début calé, ou `null` si la zone est trop courte pour la durée.
 */
export function snapStartInZone(
  clickTime: Date,
  zone: FreeZone,
  durationMin: number,
  granularityMin: number = GRANULARITY_MIN,
): Date | null {
  const durMs = durationMin * 60_000;
  const minStart = zone.debut.getTime();
  const maxStart = zone.fin.getTime() - durMs;

  // Zone trop courte pour accueillir la durée
  if (maxStart < minStart) return null;

  const granMs = granularityMin * 60_000;
  // Cale sur la grille du quart d'heure (epoch aligné :00/:15/:30/:45 en UTC)
  let start = Math.floor(clickTime.getTime() / granMs) * granMs;

  if (start < minStart) start = minStart;
  if (start > maxStart) {
    // Dernier quart d'heure qui rentre encore dans la zone
    start = Math.floor(maxStart / granMs) * granMs;
    if (start < minStart) start = minStart;
  }
  return new Date(start);
}
